import { defineComponent as _defineComponent } from 'vue'
import { computed, useListeners } from 'vue'

import MapMarker from '@/components/core/map/MapMarker.vue'
import MapMarkerLegacy from '@/components/core/map/MapMarkerLegacy.vue'
import MapPolyline from '@/components/core/map/MapPolyline.vue'
import { formattedDistance } from '@/utils/utility-manager'
import { DistanceUnit, TravelMode } from '@/utils/enums'

type MapRouteEvents = {
  (e: 'marker-click', event: google.maps.MapMouseEvent): void
  (e: 'marker-drag', event: google.maps.MapMouseEvent): void
  (e: 'marker-dragend', event: google.maps.MapMouseEvent): void
  (e: 'marker-dragstart'): void
  (e: 'marker-gmp-click'): google.maps.marker.AdvancedMarkerClickEvent // only available with v=beta
  (e: 'polyline-click', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-contextmenu', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-dblclick', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-drag', event: google.maps.MapMouseEvent): void
  (e: 'polyline-dragend', event: google.maps.MapMouseEvent): void
  (e: 'polyline-dragstart', event: google.maps.MapMouseEvent): void
  (e: 'polyline-mousedown', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-mouseout', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-mousemove', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-mouseover', event: google.maps.PolyMouseEvent): void
  (e: 'polyline-mouseup', event: google.maps.PolyMouseEvent): void
}

export interface MapRouteProps {
  distance: {
    value: number
    unit: DistanceUnit
  }
  center: {
    lat: number
    lng: number
  }
  travelMode: TravelMode
  emoji: string
  hide?: boolean
  hideLabel?: boolean
  markerOptions?: google.maps.marker.AdvancedMarkerElementOptions
  polylineOptions?: google.maps.PolylineOptions
}

export default /*#__PURE__*/_defineComponent({
  __name: 'MapRoute',
  props: {
    distance: null,
    center: null,
    travelMode: null,
    emoji: null,
    hide: { type: Boolean },
    hideLabel: { type: Boolean },
    markerOptions: null,
    polylineOptions: null
  },
  emits: ["marker-click", "marker-drag", "marker-dragend", "marker-dragstart", "marker-gmp-click", "polyline-click", "polyline-contextmenu", "polyline-dblclick", "polyline-drag", "polyline-dragend", "polyline-dragstart", "polyline-mousedown", "polyline-mouseout", "polyline-mousemove", "polyline-mouseover", "polyline-mouseup"],
  setup(__props: any) {

const props = __props as {
  distance: {
    value: number
    unit: DistanceUnit
  }
  center: {
    lat: number
    lng: number
  }
  travelMode: TravelMode
  emoji: string
  hide?: boolean
  hideLabel?: boolean
  markerOptions?: google.maps.marker.AdvancedMarkerElementOptions
  polylineOptions?: google.maps.PolylineOptions
};





// -------- Split Listeners for Marker and Polyline
const listeners = useListeners()

const markerListeners = computed(() => {
  return reduceListenersByPrefix(listeners, 'marker-')
})

const polylineListeners = computed(() => {
  return reduceListenersByPrefix(listeners, 'polyline-')
})

// -------- Circle Positions
const startCirclePosition = computed(() => {
  if (!props.polylineOptions?.path) return null

  const path = props.polylineOptions.path as
    | google.maps.LatLng[]
    | google.maps.LatLngLiteral[]
  if (!path || path.length === 0) return null

  const firstPoint = path[0]
  if (firstPoint instanceof google.maps.LatLng) {
    return { lat: firstPoint.lat(), lng: firstPoint.lng() }
  }
  return firstPoint
})

const endCirclePosition = computed(() => {
  if (!props.polylineOptions?.path) return null

  const path = props.polylineOptions.path as
    | google.maps.LatLng[]
    | google.maps.LatLngLiteral[]
  if (!path || path.length === 0) return null

  const lastPoint = path[path.length - 1]
  if (lastPoint instanceof google.maps.LatLng) {
    return { lat: lastPoint.lat(), lng: lastPoint.lng() }
  }
  return lastPoint
})

// -------- Circle Marker Icons (Legacy)

const circleIcon = computed(() => ({
  path: 0, // google.maps.SymbolPath.CIRCLE
  fillColor: '#FFFFFF',
  fillOpacity: 1,
  strokeColor: props.polylineOptions?.strokeColor ?? '#FFC94A',
  strokeWeight: 2,
  scale: 3.5,
}))

/**
 * Filters listeners by prefix.
 *
 * The listeners object is filtered by the prefix of the event listeners name
 * and also removes prefix of the matching listeners.
 * @param listeners The listeners object, which contains the event listeners.
 * @param prefix The prefix of the event listeners name, by which the listeners
 * are reduced.
 */
function reduceListenersByPrefix(
  listeners: Record<string, Function | Function[]>,
  prefix: string,
) {
  return Object.entries(listeners).reduce(
    (acc, [key, value]) => {
      if (key.startsWith(prefix)) {
        acc[key.replace(prefix, '')] = value
      }
      return acc
    },
    {} as Record<string, Function | Function[]>,
  )
}

return { __sfc: true,props, listeners, markerListeners, polylineListeners, startCirclePosition, endCirclePosition, circleIcon, reduceListenersByPrefix, MapMarker, MapMarkerLegacy, MapPolyline, formattedDistance }
}

})